// import axios from "axios";
import apiClient from "./apiClient";
import publicApiClient from "./publicApiClient";
import {
  MerchantBusinessType,
  MoneySettlementPlatforms,
  SettlementCountry,
  SettlementMode,
} from "types/moneySettlement";
import { NETWORK_NAME, NetworkType } from "./network";
import { TokenSupport } from "types/tokenSupport";
import { ApprovalAudit, GrantResult } from "types/approvals";

const RECENT_CARDS_LIMIT = 10;
const RECENT_MERCHANTS_LIMIT = 10;
const RECENT_REDEEMS_LIMIT = 10;
const RECENT_TERMINALS_LIMIT = 10;

// Server-owned constants (browser RPC endpoint, ...). Loaded once at boot by
// features/config/useAppConfig, which hands the RPC URL to the @gardio libs.
export async function getAppConfig(network: NetworkType) {
  return apiClient.get("/appConfig", {
    params: {
      network_name: NETWORK_NAME,
      network_type: network,
    },
  });
}

export async function getEmailByIssuerId(
  issuerId: string,
): Promise<string | null> {
  const res = await publicApiClient.post("/auth/get-email", { issuerId });
  return res.data ?? null;
}

export interface IssueCardPayload {
  cardAddress: string;
  cardNumber: string;
  token: string;
}

export interface UpdateCardDataPayload {
  cardNumber: string;
}

export interface SignUpUserPayload {
  cardNumber: string;
  email: string;
}

export interface PrepareCardIssuancePayload {
  cardNumber: string;
  email: string;
}

/** A refusal (card held by someone else, not Manufactured on-chain) arrives as an ERROR, not a decision. */
export interface PrepareCardIssuanceResponse {
  decision: "activate" | "verify_email";
  /** The normalised card number the backend actually acted on. */
  cardNumber: string;
  /** Present on "activate": the cardholder the card belongs to. */
  uid?: string;
}

export interface VerifyEmailPayload {
  email: string;
}

export interface VerifyEmailResponse {
  /** How long the mailed code stays valid, in ms. */
  expiresInMs: number;
}

export interface VerifyOtpPayload {
  email: string;
  otp: string;
}

export interface AddMerchantPayload {
  treasuryId: string;
  merchantName: string;
  /** ISO 3166-1 numeric country code, e.g. 840 for the United States. */
  country: number;
  /** ISO 18245 merchant category code (MCC), e.g. 5411 for grocery stores. */
  mcc: number;
  /** True for externally-managed merchants; the inverse of "managed account". */
  isExternal: boolean;
}

/** Response from the step-1 addMerchant call. */
export interface AddMerchantResponse {
  merchantId: string;
  merchantAddress: string;
  treasuryId: string;
  txHash: string | null;
  /** Present when the backend resumed a previously-started onboarding. */
  resumed?: boolean;
}

export interface SignUpMerchantPayload {
  merchantId: string;
  email: string;
  merchantDesc: string;
  platformId: MoneySettlementPlatforms;
  country: SettlementCountry;
  businessType: MerchantBusinessType;
  settlementMode: SettlementMode;
}

export interface PrepareMerchantSignupPayload {
  merchantId: string;
  email: string;
  merchantDesc: string;
  platformId: MoneySettlementPlatforms;
  country: SettlementCountry;
  settlementMode: SettlementMode;
}

/**
 * What the backend's sanity checks decided. The only non-refusal is "verify_email": run the full
 * gate. A refusal (the id is already registered, registered to someone else, or the address
 * already owns a merchant) arrives as an ERROR, not a decision.
 */
export interface PrepareMerchantSignupResponse {
  decision: "verify_email";
  merchantId: string;
}

export interface CardApiRecord {
  cardNumber: string;
  address: string;
  aliasAddress: string;
  cardStatus: number;
  counter: number;
  status:
    | "active"
    | "blocked"
    | "pending"
    | "Revoked"
    | "Active"
    | "Blocked"
    | "Pending"
    | "Manufactured"
    | "Suspended"
    | "Expired";
}

export interface MerchantApiRecord {
  merchantId: string;
  merchantAddress: string;
  merchantName: string;
  merchantStatus: number;
  /** True once the merchant has completed Firebase signup (add-merchant step 2). */
  signedUp: boolean;
}

// Settlement metadata read from the merchant's Firebase doc, fetched lazily per
// row for the merchants-list expandable detail. platformId/settlementMode are
// null until the merchant has signed up (no Firebase doc yet).
export interface MerchantSettlementInfo {
  merchantId: string;
  signedUp: boolean;
  platformId: MoneySettlementPlatforms | null;
  settlementMode: SettlementMode | null;
}

export interface SpecificMerchant extends MerchantApiRecord {
  balance: number;
}
export interface SpecificCard {
  /** false when the PAN is not registered on-chain; the miss body carries no other field. */
  found?: boolean;
  cardNumber: string;
  address: string;
  aliasAddress: string;
  balance: number;
  cardStatus: number;
  counter: number;
  status:
    | "active"
    | "blocked"
    | "pending"
    | "Revoked"
    | "Active"
    | "Blocked"
    | "Pending"
    | "Manufactured"
    | "Unassigned"
    | "Suspended"
    | "Expired";
}

/**
 * GET /posCountByTreasury. A BREAKDOWN, not a count — there is no pos→treasury link on chain, so
 * the route joins pos→merchant→treasury and reports the whole join. `posCount` is the total.
 */
export interface PosCountByTreasury {
  treasuryId: string;
  posCount: number;
  merchantsCount: number;
  byStatus: { idle: number; active: number; blocked: number };
  byMerchant: { merchantId: string; posCount: number }[];
}

export interface PosByTreasury {
  serialId: string;
  serial: string;
  merchantId: string;
  /** "" for stock; absent from a backend that predates the field. */
  merchantName?: string;
  status: number;
  statusLabel: "UNASSIGNED" | "IDLE" | "ACTIVE" | "BLOCKED";
  registeredAt: number;
  unassigned: boolean;
  vendorDigit: number;
  typeDigit: number;
  // merchantPosSeq: number;
  // fullPosId: string;
  // completeId: string;
}

export async function cardsCountApi(network: NetworkType) {
  return await apiClient.get<number>("/cardsCount", {
    params: { network_name: NETWORK_NAME, network_type: network },
  });
}

export async function merchantsCountApi(
  treasuryId: string,
  network: NetworkType,
) {
  return apiClient.get<number>("/merchantsCount", {
    params: { treasuryId, network_name: NETWORK_NAME, network_type: network },
  });
}

export async function terminalsCountApi(
  treasuryId: string,
  network: NetworkType,
) {
  return apiClient.get<PosCountByTreasury>("/posCountByTreasury", {
    params: {
      treasuryId,
      network_name: NETWORK_NAME,
      network_type: network,
    },
  });
}

export async function getTerminalsApi(
  treasuryId: string,
  network: NetworkType,
) {
  return apiClient.get<PosByTreasury[]>("/recentPosByTreasury", {
    params: {
      treasuryId,
      network_name: NETWORK_NAME,
      network_type: network,
      limit: RECENT_TERMINALS_LIMIT,
    },
  });
}

export async function blockTerminalApi(serialId: string, network: NetworkType) {
  return apiClient.post("/blockPOS", {
    serialId,
    network_name: NETWORK_NAME,
    network_type: network,
  });
}

export async function lookupTerminalApi(
  treasuryId: string,
  serial: string,
  network: NetworkType,
) {
  return apiClient.get<PosByTreasury>("/posBySerialForTreasury", {
    params: {
      treasuryId,
      serial,
      network_name: NETWORK_NAME,
      network_type: network,
    },
  });
}

export async function lookupCardApi(
  cardNumber: string,
  network: NetworkType,
  token?: string,
) {
  return apiClient.get("/lookupCard", {
    params: {
      cardNumber,
      network_name: NETWORK_NAME,
      network_type: network,
      token,
    },
  });
}

export async function getCards(network: NetworkType): Promise<CardApiRecord[]> {
  const res = await apiClient.get("/recentCards", {
    params: {
      network_name: NETWORK_NAME,
      network_type: network,
      limit: RECENT_CARDS_LIMIT,
    },
  });
  return res.data;
}

export async function lookupMerchantApi(
  merchantId: string,
  network: NetworkType,
  token?: string,
) {
  return apiClient.get("/lookupMerchant", {
    params: {
      merchantId,
      network_name: NETWORK_NAME,
      network_type: network,
      token,
    },
  });
}

export async function getMerchants(
  treasuryId: string,
  network: NetworkType,
): Promise<MerchantApiRecord[]> {
  const res = await apiClient.get("/merchantsByTreasury", {
    params: {
      treasuryId,
      network_name: NETWORK_NAME,
      network_type: network,
      limit: RECENT_MERCHANTS_LIMIT,
    },
  });
  return res.data;
}

export async function getMerchantSettlement(
  merchantId: string,
): Promise<MerchantSettlementInfo> {
  const res = await apiClient.get<MerchantSettlementInfo>(
    "/merchantSettlement",
    { params: { merchantId } },
  );
  return res.data;
}

export async function getTokenSupport(
  token: string,
  network: NetworkType,
): Promise<TokenSupport> {
  const res = await apiClient.get("/token/support", {
    params: {
      token,
      network_name: NETWORK_NAME,
      network_type: network,
    },
  });
  return res.data;
}

export async function batchBalanceByCardNumber(
  cardNumbers: string[],
  network: NetworkType,
): Promise<Record<string, number>> {
  const res = await apiClient.post("/batchBalancesByCardNumber", {
    cardNumbers,
    network_name: NETWORK_NAME,
    network_type: network,
  });
  return res.data;
}

// One token per call: every address in the batch is read in the same token, which is what the
// backend documents. Omitted -> the contract's default token.
export async function batchBalanceByAddress(
  addresses: string[],
  network: NetworkType,
  token?: string,
): Promise<Record<string, number>> {
  const res = await apiClient.post("/batchBalancesByAddress", {
    addresses,
    network_name: NETWORK_NAME,
    network_type: network,
    token,
  });
  return res.data;
}

export async function getDeployedBalance(
  network: NetworkType,
  token?: string,
): Promise<any> {
  return apiClient.get("/deployedBalance", {
    params: {
      network_name: NETWORK_NAME,
      network_type: network,
      token,
    },
  });
}

export async function redeemedBalanceApi(network: NetworkType, token?: string) {
  return apiClient.get("/redeemedBalance", {
    params: {
      network_name: NETWORK_NAME,
      network_type: network,
      token,
    },
  });
}

export async function issueNewCard(
  payload: IssueCardPayload,
  network: NetworkType,
) {
  return apiClient.post("/registerNewCard", {
    ...payload,
    network_name: NETWORK_NAME,
    network_type: network,
  });
}

export async function updateCardData(
  payload: UpdateCardDataPayload,
  network: NetworkType,
  token: string,
) {
  return apiClient.post("/updateCardDataAfterManufacture", {
    ...payload,
    network_name: NETWORK_NAME,
    network_type: network,
    token,
  });
}

/** Issuance preflight — call before anything else in the issue-card flow. */
export async function prepareCardIssuance(
  payload: PrepareCardIssuancePayload,
  network: NetworkType,
) {
  return apiClient.post<PrepareCardIssuanceResponse>("/prepareCardIssuance", {
    ...payload,
    network_name: NETWORK_NAME,
    network_type: network,
  });
}

export async function signupNewUser(
  payload: SignUpUserPayload,
  network: NetworkType,
) {
  return apiClient.post("/signUpCardToUser", {
    ...payload,
    network_name: NETWORK_NAME,
    network_type: network,
  });
}

/** Step 1 of the issuance email gate: mail a one-time code to the cardholder's address. */
export async function verifyEmail(
  payload: VerifyEmailPayload,
  network: NetworkType,
) {
  return apiClient.post<VerifyEmailResponse>("/verifyEmail", {
    ...payload,
    network_name: NETWORK_NAME,
    network_type: network,
  });
}

/** Step 2: submit the code. Success marks the address spendable by /signUpCardToUser. */
export async function verifyOtp(
  payload: VerifyOtpPayload,
  network: NetworkType,
) {
  return apiClient.post("/verifyOtp", {
    ...payload,
    network_name: NETWORK_NAME,
    network_type: network,
  });
}

export async function addNewMerchant(
  payload: AddMerchantPayload,
  network: NetworkType,
) {
  return apiClient.post<AddMerchantResponse>("/registerMerchant", {
    ...payload,
    version: 1,
    network_name: NETWORK_NAME,
    network_type: network,
  });
}

/** Merchant-signup preflight — call before anything else in the add-merchant flow. */
export async function prepareMerchantSignup(
  payload: PrepareMerchantSignupPayload,
) {
  return apiClient.post<PrepareMerchantSignupResponse>(
    "/prepareMerchantSignup",
    payload,
  );
}

export async function signupNewMerchant(payload: SignUpMerchantPayload) {
  return apiClient.post("/signupMerchant", payload);
}

export async function suspendCardApi(cardNumber: string, network: NetworkType) {
  return apiClient.post("/suspendCard", {
    cardNumber,
    network_name: NETWORK_NAME,
    network_type: network,
  });
}

export async function activateCardApi(
  cardNumber: string,
  network: NetworkType,
) {
  return apiClient.post("/activateCard", {
    cardNumber,
    network_name: NETWORK_NAME,
    network_type: network,
  });
}

export async function blockCard(cardNumber: string, network: NetworkType) {
  return apiClient.post("/blockCard", {
    cardNumber,
    network_name: NETWORK_NAME,
    network_type: network,
  });
}

export async function replaceCardApi(
  oldCardNumber: string,
  newCardNumber: string,
  network: NetworkType,
) {
  return apiClient.post("/replaceCard", {
    oldCardNumber,
    // The backend reads `newCardNumber` (apis_keys.NEW_CARD_NUMBER) — a bare `cardNumber` here
    // is silently ignored and the request 400s with "newCardNumber is required".
    newCardNumber,
    network_name: NETWORK_NAME,
    network_type: network,
  });
}

export async function suspendMerchantApi(
  merchantId: string,
  network: NetworkType,
) {
  return apiClient.post("/suspendMerchant", {
    merchantId,
    network_name: NETWORK_NAME,
    network_type: network,
  });
}

export async function activateMerchantApi(
  merchantId: string,
  network: NetworkType,
) {
  return apiClient.post("/activateMerchant", {
    merchantId,
    network_name: NETWORK_NAME,
    network_type: network,
  });
}

export async function blockMerchant(merchantId: string, network: NetworkType) {
  return apiClient.post("/blockMerchant", {
    merchantId,
    network_name: NETWORK_NAME,
    network_type: network,
  });
}

export async function lookupRedeemApi(requestId: string, network: NetworkType) {
  return apiClient.get("/lookupRedeem", {
    params: { requestId, network_name: NETWORK_NAME, network_type: network },
  });
}
// `token` filters to one settlement token; omitted returns EVERY token. The backend applies it
// DURING the tail walk, so `token=USDC&limit=20` yields 20 USDC redeems rather than 20 rows of
// any token then filtered down to a handful.
export async function getRedeemRequests(
  treasuryId: string,
  network: NetworkType,
  token?: string,
): Promise<any[]> {
  const res = await apiClient.get("/redeem/recent", {
    params: {
      treasuryId,
      network_name: NETWORK_NAME,
      network_type: network,
      limit: RECENT_REDEEMS_LIMIT,
      token,
    },
  });
  return res.data;
}

export async function approveRedeemRequest(
  payload: {
    requestId: string;
    merchantId: string;
    stripeAmount: number;
  },
  network: NetworkType,
) {
  return apiClient.post("/redeem/approve", {
    ...payload,
    network_name: NETWORK_NAME,
    network_type: network,
  });
}

export async function rejectRedeemRequest(
  payload: {
    requestId: string;
    stripeTxId?: string;
    success?: boolean;
  },
  network: NetworkType,
) {
  return apiClient.post("/redeem/finalize", {
    ...payload,
    network_name: NETWORK_NAME,
    network_type: network,
  });
}

export async function checkCardTokenApprovals(
  cardNumber: string,
  network: NetworkType,
) {
  return apiClient.post<ApprovalAudit>("/admin/approvals/cards", {
    cardNumber,
    network_name: NETWORK_NAME,
    network_type: network,
  });
}

export async function checkMerchantTokenApprovals(
  merchantId: string,
  network: NetworkType,
) {
  return apiClient.post<ApprovalAudit>("/admin/approvals/merchants", {
    merchantId,
    network_name: NETWORK_NAME,
    network_type: network,
  });
}

export async function grantCardTokenAproval(
  cardNumber: string,
  token: string,
  network: NetworkType,
) {
  return apiClient.post<GrantResult>("/admin/approvals/cards/grant", {
    cardNumber,
    token,
    network_name: NETWORK_NAME,
    network_type: network,
  });
}

export async function grantMerchantTokenAproval(
  merchantId: string,
  token: string,
  network: NetworkType,
) {
  return apiClient.post<GrantResult>("/admin/approvals/merchants/grant", {
    merchantId,
    token,
    network_name: NETWORK_NAME,
    network_type: network,
  });
}
